import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';

import { CronScheduleMigrationNotice } from '@/features/schedules/components/CronScheduleMigrationNotice';
import { UtcTimeDisplay } from '@/features/schedules/components/UtcTimeDisplay';
import { parseAdvancedCronForEdit } from '@/features/schedules/resolvers/advancedCronResolver';
import type { AdvancedCronEditableScope } from '@/features/schedules/types/advancedCronMigration';
import { buildCronTimeFrameFromForm } from '@/features/schedules/utils/cronTimeFrame';
import { analyzeLegacyCronMigration } from '@/features/schedules/utils/legacyCronMigration';
import {
  buildLocalRecurringCronExpression,
  describeUtcCronExpression,
  recurringUiFieldsToFormState,
} from '@/features/schedules/utils/recurringCronSchedule';
import {
  computeLocalTimeFrameDurationHours,
  formatTimeFrameEndDropdownLabel,
  isCrossMidnightLocalTimeFrame,
  validateRecurringTimeFrame,
} from '@/features/schedules/utils/recurringCronValidation';
import { ADVANCED_CRON_MIGRATION_TF_CONFIRM_REQUIRED } from '@/features/schedules/utils/scheduleDefaults';
import {
  convertLocalTimeToUTC,
  convertLocalWallClockScheduleToUtcParts,
  formatTimezoneLabel,
} from '@/features/schedules/utils/timezoneData';
import type { CronTimeFrame } from '@/types/schedule';

/** Fixed label column width for aligned recurring-tab form rows */
const RECURRING_LABEL_WIDTH = 120;

const recurringFieldRowSx = {
  display: 'grid',
  gridTemplateColumns: `${RECURRING_LABEL_WIDTH}px 1fr`,
  alignItems: 'center',
  columnGap: 2,
} as const;

export interface CronScheduleBuilderValue {
  expression: string;
  explain: string;
  /** Live UTC expression for preview — updated on every valid form change; save uses buildForSave(). */
  previewExpression?: string;
  /** Bumps when any builder control affecting preview changes (even if cron bytes stay the same). */
  previewConfigKey?: string;
  /** True after the user changes builder controls (edit passthrough when false). */
  isDirty?: boolean;
  /** False when recurring validation fails after a user edit. */
  isValid?: boolean;
  /** Original API expression — preserved on edit when !isDirty. */
  canonicalExpression?: string;
}

/** Advanced cron form fields use the selected author timezone (converted to UTC on save). */
export interface CronScheduleBuilderProps {
  value: CronScheduleBuilderValue;
  onChange: (value: CronScheduleBuilderValue) => void;
  /** IANA timezone for authoring local wall-clock fields. */
  timeZone: string;
  /** When set, enables legacy migration analysis (requires stored schedule TZ). */
  storedCronTimeZone?: string;
  /** Stored author-local time frame — hydrates Starting/Ending at on edit. */
  storedCronTimeFrame?: CronTimeFrame | null;
  disabled?: boolean;
  compact?: boolean;
  /** When false, hides legacy migration banner (create + recipe editor). */
  showLegacyMigrationNotice?: boolean;
  /** Migration step — locks builder controls until timezone/time frame confirmed. */
  editableScope?: AdvancedCronEditableScope;
  /** When true during TF pending step, timeframe fields show error styling. */
  timeframeUnconfirmed?: boolean;
  /** Step 2 migration — rendered below recurring time frame controls. */
  onConfirmTimeframe?: () => void;
}

type ScheduleMode = 'recurring' | 'fixed-time' | 'recurring-fixed-time';

export type CronBuilderScheduleMode = ScheduleMode;

export interface CronScheduleBuilderBuildResult {
  expression: string;
  explain: string;
  isValid: boolean;
  mode: ScheduleMode;
  /** True only after the user changed a control (not tab switches alone). */
  userEdited: boolean;
  /** Set when recurring + time frame; omitted otherwise. */
  cronTimeFrame?: CronTimeFrame | null;
}

export interface CronScheduleBuilderHandle {
  buildForSave: () => CronScheduleBuilderBuildResult;
}

type FrequencyUnit = 'minutes' | 'hours';
type OnType = 'dayOfWeek' | 'dayOfMonth' | 'month';

// ---------------------------------------------------------------------------
// Constants - matching legacy exactly
// ---------------------------------------------------------------------------

// Recurring minute frequency options (multiples of 5, value format: "*/N")
const RECURRING_MINUTE_OPTIONS = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((n) => ({
  value: `*/${n}`,
  label: String(n),
}));

// Recurring hour frequency options (value format: "*/N")
const RECURRING_HOUR_OPTIONS = Array.from({ length: 23 }, (_, i) => ({
  value: `*/${i + 1}`,
  label: String(i + 1),
}));

const DEFAULT_RECURRING_HOURS = '*/1';
const DEFAULT_RECURRING_MINUTES = '*/5';

function ensureRecurringOptionValue(
  value: string,
  options: { value: string }[],
  fallback: string
): string {
  return options.some((opt) => opt.value === value) ? value : fallback;
}

// Starting-minute options for hourly mode (multiples of 5 from 0-55)
const STARTING_MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: i * 5,
  label: String(i * 5).padStart(2, '0'),
}));

/** Hour dropdown options in 12h format for time-frame selectors (includes midnight). */
const HOUR_OPTIONS_12H = Array.from({ length: 24 }, (_, i) => {
  let label: string;
  if (i === 0) label = '12AM';
  else if (i < 12) label = `${i}AM`;
  else if (i === 12) label = '12PM';
  else label = `${i - 12}PM`;
  return { value: i, label };
});

/** Fixed-time minute options (multiples of 5, 00-55) */
const FIXED_MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: i * 5,
  label: String(i * 5).padStart(2, '0'),
}));

/** Fixed-time hour options (0-23 with leading zero) */
const FIXED_HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: String(i).padStart(2, '0'),
}));

/** Day-of-week options (1=Mon .. 7=Sun, matching legacy) */
const DAY_OF_WEEK_OPTIONS = [
  { value: '*', label: 'every day' },
  { value: '1-5', label: 'Mondays to Fridays' },
  { value: '6,7', label: 'Saturdays and Sundays' },
  { value: '1', label: 'Mondays' },
  { value: '2', label: 'Tuesdays' },
  { value: '3', label: 'Wednesdays' },
  { value: '4', label: 'Thursdays' },
  { value: '5', label: 'Fridays' },
  { value: '6', label: 'Saturdays' },
  { value: '7', label: 'Sundays' },
];

/** Day-of-month options (legacy: "every month day", "last day", 1-31) */
const DAY_OF_MONTH_OPTIONS = [
  { value: '*', label: 'every month day' },
  { value: 'L', label: 'last day of month' },
  ...Array.from({ length: 31 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1),
  })),
];

/** Month options */
const MONTH_OPTIONS = [
  { value: '*', label: 'every month' },
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

// ---------------------------------------------------------------------------
// Expression parser – restores UTC form state from stored cron expression
// ---------------------------------------------------------------------------

interface ParsedState {
  mode: ScheduleMode;
  frequencyUnit: FrequencyUnit;
  recurringMinutes: string;
  recurringHours: string;
  startingMinute: number;
  useTimeFrame: boolean;
  hoursFrom: number;
  hoursTo: number;
  onType: OnType;
  dayOfWeek: string;
  dayOfMonth: string;
  month: string;
  fixedHour: number;
  fixedMinute: number;
  fixedDate: Date;
  fixedYear: string;
}

/** Derive which "On:" selector is active from the non-wildcard cron field. */
function deriveOnType(dayOfMonth: string, month: string): OnType {
  if (dayOfMonth && dayOfMonth !== '*' && dayOfMonth !== '?') return 'dayOfMonth';
  if (month && month !== '*') return 'month';
  return 'dayOfWeek';
}

function parseExistingExpression(
  expression: string,
  timeZone: string,
  cronTimeFrame?: CronTimeFrame | null
): ParsedState {
  const defaults: ParsedState = {
    mode: 'recurring',
    frequencyUnit: 'hours',
    recurringMinutes: '*/5',
    recurringHours: '*/1',
    startingMinute: 0,
    useTimeFrame: false,
    hoursFrom: 9,
    hoursTo: 17,
    onType: 'dayOfWeek',
    dayOfWeek: '1-5',
    dayOfMonth: '*',
    month: '*',
    fixedHour: new Date().getUTCHours(),
    fixedMinute: 0,
    fixedDate: new Date(),
    fixedYear: '*',
  };

  if (!expression || expression.trim() === '') {
    return defaults;
  }

  const parsed = parseAdvancedCronForEdit(expression, timeZone, cronTimeFrame);
  if (!parsed) {
    return defaults;
  }

  if (parsed.mode === 'recurring') {
    return {
      ...defaults,
      mode: 'recurring',
      frequencyUnit: parsed.frequencyUnit ?? 'hours',
      recurringHours:
        parsed.frequencyUnit === 'hours'
          ? ensureRecurringOptionValue(
              `*/${parsed.frequency ?? 1}`,
              RECURRING_HOUR_OPTIONS,
              DEFAULT_RECURRING_HOURS
            )
          : defaults.recurringHours,
      recurringMinutes:
        parsed.frequencyUnit === 'minutes'
          ? ensureRecurringOptionValue(
              `*/${parsed.frequency ?? 5}`,
              RECURRING_MINUTE_OPTIONS,
              DEFAULT_RECURRING_MINUTES
            )
          : defaults.recurringMinutes,
      startingMinute: parsed.startingMinute ?? 0,
      useTimeFrame: parsed.useTimeFrame ?? false,
      hoursFrom: parsed.hoursFrom ?? 9,
      hoursTo: parsed.hoursTo ?? 17,
      onType: deriveOnType(parsed.dayOfMonth ?? '*', parsed.month ?? '*'),
      dayOfWeek: parsed.dayOfWeek ?? '1-5',
      dayOfMonth: parsed.dayOfMonth ?? '*',
      month: parsed.month ?? '*',
    };
  }

  if (parsed.mode === 'fixed-time') {
    const year = parsed.fixedYear ? parseInt(parsed.fixedYear, 10) : new Date().getUTCFullYear();
    const month = parsed.fixedMonth ?? 1;
    const day = parsed.fixedDay ?? 1;

    return {
      ...defaults,
      mode: 'fixed-time',
      fixedHour: parsed.fixedHour ?? 9,
      fixedMinute: parsed.fixedMinute ?? 0,
      fixedDate: new Date(year, month - 1, day),
      fixedYear: parsed.fixedYear ?? '*',
      dayOfMonth: parsed.dayOfMonth ?? '*',
      month: parsed.month ?? '*',
    };
  }

  return {
    ...defaults,
    mode: 'recurring-fixed-time',
    fixedHour: parsed.fixedHour ?? 0,
    fixedMinute: parsed.fixedMinute ?? 0,
    onType: deriveOnType(parsed.dayOfMonth ?? '*', parsed.month ?? '*'),
    dayOfWeek: parsed.dayOfWeek ?? '*',
    dayOfMonth: parsed.dayOfMonth ?? '*',
    month: parsed.month ?? '*',
  };
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const CronScheduleBuilder = forwardRef<CronScheduleBuilderHandle, CronScheduleBuilderProps>(
  (
    {
      value,
      onChange,
      timeZone,
      storedCronTimeZone,
      storedCronTimeFrame,
      disabled = false,
      showLegacyMigrationNotice = false,
      editableScope,
      timeframeUnconfirmed = false,
      onConfirmTimeframe,
    },
    ref
  ) => {
    const scope = editableScope ?? 'timezone-and-timeframe';
    const builderFullyDisabled = disabled || scope === 'timezone-pending-confirmation';
    const timeframeOnlyMode = scope === 'timeframe-pending-confirmation';
    const nonTimeframeDisabled = builderFullyDisabled || timeframeOnlyMode;
    const timeframeConfirmPending = timeframeOnlyMode && timeframeUnconfirmed;

    // Parse initial state from the provided expression
    const initialState = useMemo(
      () => parseExistingExpression(value.expression, timeZone, storedCronTimeFrame),
      [storedCronTimeFrame, value.expression, timeZone]
    );

    // Mode
    const [mode, setMode] = useState<ScheduleMode>(initialState.mode);

    // Recurring state
    const [frequencyUnit, setFrequencyUnit] = useState<FrequencyUnit>(initialState.frequencyUnit);
    const [recurringMinutes, setRecurringMinutes] = useState(initialState.recurringMinutes);
    const [recurringHours, setRecurringHours] = useState(initialState.recurringHours);
    const [startingMinute, setStartingMinute] = useState(initialState.startingMinute);
    const [useTimeFrame, setUseTimeFrame] = useState(initialState.useTimeFrame);
    const [hoursFrom, setHoursFrom] = useState(initialState.hoursFrom);
    const [hoursTo, setHoursTo] = useState(initialState.hoursTo);

    // Recurring date selectors
    const [onType, setOnType] = useState<OnType>(initialState.onType);
    const [recurringDow, setRecurringDow] = useState(initialState.dayOfWeek);
    const [recurringDom, setRecurringDom] = useState(initialState.dayOfMonth);
    const [recurringMonth, setRecurringMonth] = useState(initialState.month);

    // Fixed-time state
    const [fixedDate, setFixedDate] = useState<Date>(initialState.fixedDate);
    const [fixedTime, setFixedTime] = useState<Date>(() => {
      const d = new Date();
      d.setHours(initialState.fixedHour, initialState.fixedMinute, 0, 0);
      return d;
    });

    // Recurring fixed-time state
    const [rfHour, setRfHour] = useState(initialState.fixedHour);
    const [rfMinute, setRfMinute] = useState(initialState.fixedMinute);
    const [rfOnType, setRfOnType] = useState<OnType>(initialState.onType);
    const [rfDow, setRfDow] = useState(initialState.dayOfWeek);
    const [rfDom, setRfDom] = useState(initialState.dayOfMonth);
    const [rfMonth, setRfMonth] = useState(initialState.month);

    // Track if the user has changed builder controls (gates onChange + edit passthrough).
    const [isDirty, setIsDirty] = useState(value.isDirty ?? false);
    const userEditedRef = useRef(Boolean(value.isDirty));
    const canonicalExpressionRef = useRef(value.canonicalExpression ?? value.expression);

    const markDirty = useCallback(() => {
      userEditedRef.current = true;
      setIsDirty(true);
    }, []);

    /** Legacy PresetTab.onPickTimeUnit — reset Every dropdown when switching Minutes/Hours. */
    const handleFrequencyUnitChange = useCallback(
      (_: React.MouseEvent<HTMLElement>, unit: FrequencyUnit | null) => {
        if (!unit) return;
        markDirty();
        if (unit === 'hours') {
          setRecurringHours(DEFAULT_RECURRING_HOURS);
        } else {
          setRecurringMinutes(DEFAULT_RECURRING_MINUTES);
        }
        setFrequencyUnit(unit);
      },
      [markDirty]
    );

    const handleModeChange = useCallback(
      (_event: React.SyntheticEvent, nextMode: ScheduleMode) => {
        if (nextMode === 'recurring-fixed-time' && mode === 'fixed-time') {
          setRfHour(fixedTime.getHours());
          setRfMinute(fixedTime.getMinutes());
        }
        if (nextMode === 'fixed-time' && mode === 'recurring-fixed-time') {
          setFixedTime((prev) => {
            const next = new Date(prev);
            next.setHours(rfHour, rfMinute, 0, 0);
            return next;
          });
        }
        markDirty();
        setMode(nextMode);
      },
      [fixedTime, markDirty, mode, rfHour, rfMinute]
    );

    // Track if this is the initial mount to avoid calling onChange on first render
    const isInitialMount = useRef(true);

    // The last expression this component emitted via onChange. Used to tell apart
    // our own updates (echoed back through `value`) from genuinely external changes
    // (e.g. a different schedule loaded into the dialog).
    const lastEmittedExpression = useRef(value.expression);
    const lastSyncedTimeZone = useRef(timeZone);
    const lastSyncedEditableScope = useRef(scope);

    const applyParsedState = useCallback((next: ParsedState) => {
      setMode(next.mode);
      setFrequencyUnit(next.frequencyUnit);
      setRecurringMinutes(next.recurringMinutes);
      setRecurringHours(next.recurringHours);
      setStartingMinute(next.startingMinute);
      setUseTimeFrame(next.useTimeFrame);
      setHoursFrom(next.hoursFrom);
      setHoursTo(next.hoursTo);
      setOnType(next.onType);
      setRecurringDow(next.dayOfWeek);
      setRecurringDom(next.dayOfMonth);
      setRecurringMonth(next.month);
      setFixedDate(next.fixedDate);
      const nextFixedTime = new Date();
      nextFixedTime.setHours(next.fixedHour, next.fixedMinute, 0, 0);
      setFixedTime(nextFixedTime);
      setRfHour(next.fixedHour);
      setRfMinute(next.fixedMinute);
      setRfOnType(next.onType);
      setRfDow(next.dayOfWeek);
      setRfDom(next.dayOfMonth);
      setRfMonth(next.month);
    }, []);

    // -----------------------------------------------------------------------
    // Expression builders (match legacy getExpression() + generateCronExpression())
    // -----------------------------------------------------------------------

    const buildExpression = useCallback((): string => {
      if (mode === 'fixed-time') {
        const utc = convertLocalWallClockScheduleToUtcParts(
          fixedDate.getFullYear(),
          fixedDate.getMonth() + 1,
          fixedDate.getDate(),
          fixedTime.getHours(),
          fixedTime.getMinutes(),
          timeZone
        );
        return `${utc.utcMinute} ${utc.utcHour} ${utc.utcDay} ${utc.utcMonth} * ${utc.utcYear}`;
      }

      if (mode === 'recurring-fixed-time') {
        const utc = convertLocalTimeToUTC(rfHour, rfMinute, timeZone);
        let dom = '*';
        let mon = '*';
        let dow = '*';
        if (rfOnType === 'dayOfWeek') {
          dow = rfDow;
        } else if (rfOnType === 'dayOfMonth') {
          dom = rfDom;
        } else if (rfOnType === 'month') {
          mon = rfMonth;
        }
        return `${utc.minute} ${utc.hour} ${dom} ${mon} ${dow} *`;
      }

      const dow = onType === 'dayOfWeek' ? recurringDow : '*';
      const dom = onType === 'dayOfMonth' ? recurringDom : '*';
      const mon = onType === 'month' ? recurringMonth : '*';

      return buildLocalRecurringCronExpression(
        recurringUiFieldsToFormState({
          frequencyUnit,
          recurringHours,
          recurringMinutes,
          startingMinute,
          useTimeFrame,
          hoursFrom,
          hoursTo,
          dayOfWeek: dow,
          dayOfMonth: dom,
          month: mon,
        }),
        { dayOfWeek: dow, dayOfMonth: dom, month: mon },
        timeZone
      );
    }, [
      mode,
      frequencyUnit,
      recurringMinutes,
      recurringHours,
      startingMinute,
      useTimeFrame,
      hoursFrom,
      hoursTo,
      onType,
      recurringDow,
      recurringDom,
      recurringMonth,
      fixedDate,
      fixedTime,
      rfHour,
      rfMinute,
      rfOnType,
      rfDow,
      rfDom,
      rfMonth,
      timeZone,
    ]);

    const recurringForm = useMemo(
      () =>
        recurringUiFieldsToFormState({
          frequencyUnit,
          recurringHours,
          recurringMinutes,
          startingMinute,
          useTimeFrame,
          hoursFrom,
          hoursTo,
          dayOfWeek: recurringDow,
          dayOfMonth: recurringDom,
          month: recurringMonth,
        }),
      [
        frequencyUnit,
        recurringHours,
        recurringMinutes,
        startingMinute,
        useTimeFrame,
        hoursFrom,
        hoursTo,
        recurringDow,
        recurringDom,
        recurringMonth,
      ]
    );

    const recurringValidation = useMemo(() => {
      if (mode !== 'recurring') {
        return { valid: true, errors: [] as string[] };
      }
      return validateRecurringTimeFrame(recurringForm, timeZone);
    }, [mode, recurringForm, timeZone]);

    useImperativeHandle(
      ref,
      () => ({
        buildForSave: (): CronScheduleBuilderBuildResult => {
          const isValid = mode !== 'recurring' || recurringValidation.valid;
          if (!isValid) {
            return {
              expression: value.expression,
              explain: value.explain,
              isValid: false,
              mode,
              userEdited: userEditedRef.current,
            };
          }

          try {
            const expression = buildExpression();
            const explain = describeUtcCronExpression(expression);
            const cronTimeFrame =
              mode === 'recurring'
                ? (buildCronTimeFrameFromForm(mode, recurringForm) ?? null)
                : null;
            return {
              expression,
              explain,
              isValid: true,
              mode,
              userEdited: userEditedRef.current,
              cronTimeFrame,
            };
          } catch {
            return {
              expression: value.expression,
              explain: value.explain,
              isValid: false,
              mode,
              userEdited: userEditedRef.current,
            };
          }
        },
      }),
      [
        buildExpression,
        mode,
        recurringForm,
        recurringValidation.valid,
        value.explain,
        value.expression,
      ]
    );

    const computeBuilderIsValid = useCallback((): boolean => {
      if (mode === 'recurring') {
        return recurringValidation.valid;
      }
      try {
        buildExpression();
        return true;
      } catch {
        return false;
      }
    }, [buildExpression, mode, recurringValidation.valid]);

    const previewConfigKey = useMemo(() => {
      if (mode === 'recurring-fixed-time') {
        return `rf:${rfOnType}:${rfDow}:${rfDom}:${rfMonth}:${rfHour}:${rfMinute}`;
      }
      if (mode === 'recurring') {
        return `r:${onType}:${recurringDow}:${recurringDom}:${recurringMonth}:${frequencyUnit}:${recurringHours}:${recurringMinutes}:${startingMinute}:${useTimeFrame}:${hoursFrom}:${hoursTo}`;
      }
      if (mode === 'fixed-time') {
        return `ft:${fixedDate.toISOString()}:${fixedTime.getHours()}:${fixedTime.getMinutes()}`;
      }
      return mode;
    }, [
      mode,
      rfOnType,
      rfDow,
      rfDom,
      rfMonth,
      rfHour,
      rfMinute,
      onType,
      recurringDow,
      recurringDom,
      recurringMonth,
      frequencyUnit,
      recurringHours,
      recurringMinutes,
      startingMinute,
      useTimeFrame,
      hoursFrom,
      hoursTo,
      fixedDate,
      fixedTime,
    ]);

    // Sync validation flags and live preview expression (save still commits via buildForSave()).
    useLayoutEffect(() => {
      const isValid = computeBuilderIsValid();
      let previewExpression = '';
      if (isValid) {
        try {
          previewExpression = buildExpression();
        } catch {
          previewExpression = '';
        }
      }

      const unchanged =
        value.isDirty === isDirty &&
        value.isValid === isValid &&
        value.previewExpression === previewExpression &&
        value.previewConfigKey === previewConfigKey;

      if (isInitialMount.current) {
        isInitialMount.current = false;
        const legacyMigration =
          !isDirty && mode === 'recurring' && recurringValidation.valid
            ? analyzeLegacyCronMigration(canonicalExpressionRef.current, timeZone)
            : null;
        const needsInitialPreview = !value.previewExpression?.trim() && Boolean(previewExpression);
        if (legacyMigration?.needsMigration) {
          return;
        }
        if (unchanged && !needsInitialPreview) {
          return;
        }
      } else if (unchanged) {
        return;
      }

      onChange({
        expression: value.expression,
        explain: value.explain,
        previewExpression,
        previewConfigKey,
        isDirty,
        isValid,
        canonicalExpression: canonicalExpressionRef.current,
      });
    }, [
      buildExpression,
      computeBuilderIsValid,
      isDirty,
      mode,
      onChange,
      previewConfigKey,
      recurringValidation.valid,
      timeZone,
      value.explain,
      value.expression,
      value.isDirty,
      value.isValid,
      value.previewConfigKey,
      value.previewExpression,
    ]);

    // Re-sync internal state when the expression changes from outside this
    // component (not from our own onChange) — e.g. a different schedule loaded.
    useEffect(() => {
      if (value.expression === lastEmittedExpression.current) return;
      lastEmittedExpression.current = value.expression;
      canonicalExpressionRef.current = value.canonicalExpression ?? value.expression;
      userEditedRef.current = false;
      setIsDirty(false);

      applyParsedState(parseExistingExpression(value.expression, timeZone, storedCronTimeFrame));
    }, [
      applyParsedState,
      storedCronTimeFrame,
      timeZone,
      value.canonicalExpression,
      value.expression,
    ]);

    // Re-parse local wall-clock fields when the author timezone or migration scope changes.
    useEffect(() => {
      const timeZoneChanged = timeZone !== lastSyncedTimeZone.current;
      const scopeChanged = scope !== lastSyncedEditableScope.current;
      if (!timeZoneChanged && !scopeChanged) return;
      lastSyncedTimeZone.current = timeZone;
      lastSyncedEditableScope.current = scope;
      if (userEditedRef.current) {
        return;
      }
      applyParsedState(parseExistingExpression(value.expression, timeZone, storedCronTimeFrame));
    }, [applyParsedState, scope, storedCronTimeFrame, timeZone, value.expression]);

    // -----------------------------------------------------------------------
    // Computed display values
    // -----------------------------------------------------------------------

    const storedExpression = canonicalExpressionRef.current;

    const authorTimeLabel = useMemo(() => {
      const gmt = formatTimezoneLabel(timeZone).match(/^\((GMT[^)]+)\)/)?.[1];
      return gmt ?? 'local';
    }, [timeZone]);

    const timeFrameDurationHours = useMemo(
      () => (useTimeFrame ? computeLocalTimeFrameDurationHours(hoursFrom, hoursTo) : 0),
      [useTimeFrame, hoursFrom, hoursTo]
    );

    const timeFrameCrossesMidnight = useMemo(
      () => useTimeFrame && isCrossMidnightLocalTimeFrame(hoursFrom, hoursTo),
      [useTimeFrame, hoursFrom, hoursTo]
    );

    const legacyMigration = useMemo(
      () =>
        !isDirty && mode === 'recurring' && recurringValidation.valid
          ? analyzeLegacyCronMigration(storedExpression, storedCronTimeZone)
          : null,
      [isDirty, mode, recurringValidation.valid, storedCronTimeZone, storedExpression]
    );

    // -----------------------------------------------------------------------
    // On-type selector shared between recurring and recurring-fixed-time
    // -----------------------------------------------------------------------

    const renderOnSelector = (
      currentOnType: OnType,
      setCurrentOnType: (v: OnType) => void,
      currentDow: string,
      setCurrentDow: (v: string) => void,
      currentDom: string,
      setCurrentDom: (v: string) => void,
      currentMon: string,
      setCurrentMon: (v: string) => void
    ) => {
      const handleOnTypeChange = (e: SelectChangeEvent) => {
        markDirty();
        const nextType = e.target.value as OnType;
        setCurrentOnType(nextType);
        // Clear inactive dimensions so hidden state cannot leak across on-type switches.
        if (nextType === 'dayOfWeek') {
          setCurrentDom('*');
          setCurrentMon('*');
        } else if (nextType === 'dayOfMonth') {
          setCurrentDow('*');
          setCurrentMon('*');
        } else {
          setCurrentDow('*');
          setCurrentDom('*');
        }
      };

      return (
        <Box sx={recurringFieldRowSx}>
          <Typography variant="body2" color="text.secondary">
            On
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={currentOnType}
                onChange={handleOnTypeChange}
                disabled={nonTimeframeDisabled}
              >
                <MenuItem value="dayOfWeek">day of week</MenuItem>
                <MenuItem value="dayOfMonth">day of month</MenuItem>
                <MenuItem value="month">month</MenuItem>
              </Select>
            </FormControl>

            {currentOnType === 'dayOfWeek' && (
              <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
                <Select
                  value={currentDow}
                  onChange={(e) => {
                    markDirty();
                    setCurrentDow(e.target.value);
                  }}
                  disabled={nonTimeframeDisabled}
                >
                  {DAY_OF_WEEK_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {currentOnType === 'dayOfMonth' && (
              <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
                <Select
                  value={currentDom}
                  onChange={(e) => {
                    markDirty();
                    setCurrentDom(e.target.value);
                  }}
                  disabled={nonTimeframeDisabled}
                >
                  {DAY_OF_MONTH_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {currentOnType === 'month' && (
              <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
                <Select
                  value={currentMon}
                  onChange={(e) => {
                    markDirty();
                    setCurrentMon(e.target.value);
                  }}
                  disabled={nonTimeframeDisabled}
                >
                  {MONTH_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </Box>
      );
    };

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          {/* Mode Tabs – matching legacy: Recurring | Fixed-time | Recurring Fixed-time */}
          <Tabs
            value={mode}
            onChange={handleModeChange}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Recurring" value="recurring" disabled={nonTimeframeDisabled} />
            <Tab label="Fixed-time" value="fixed-time" disabled={nonTimeframeDisabled} />
            <Tab
              label="Recurring Fixed-time"
              value="recurring-fixed-time"
              disabled={nonTimeframeDisabled}
            />
          </Tabs>

          {/* ============================================================== */}
          {/* Recurring Mode */}
          {/* ============================================================== */}
          {mode === 'recurring' && (
            <Stack spacing={2} divider={<Divider flexItem />}>
              <UtcTimeDisplay />

              <Box
                sx={
                  timeframeConfirmPending
                    ? (theme) => ({
                        border: 1,
                        borderColor: 'error.main',
                        borderRadius: 1,
                        p: 2,
                        bgcolor: alpha(theme.palette.error.main, 0.08),
                      })
                    : undefined
                }
              >
                {timeframeConfirmPending && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {ADVANCED_CRON_MIGRATION_TF_CONFIRM_REQUIRED}
                  </Alert>
                )}

                <Stack spacing={2} divider={<Divider flexItem />}>
                  {/* Time frame */}
                  <Box>
                    <FormControl component="fieldset" variant="standard">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={useTimeFrame}
                            onChange={(e) => {
                              markDirty();
                              setUseTimeFrame(e.target.checked);
                            }}
                            disabled={builderFullyDisabled}
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="body2" color="text.secondary">
                            Within a time frame
                          </Typography>
                        }
                        sx={{ ml: 0, mb: useTimeFrame ? 1.5 : 0 }}
                      />
                    </FormControl>

                    {useTimeFrame && (
                      <Box sx={{ pl: 3.5 }}>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            gap: 2,
                          }}
                        >
                          <FormControl size="small" fullWidth>
                            <InputLabel id="recurring-hours-from-label">
                              Starting at ({authorTimeLabel})
                            </InputLabel>
                            <Select
                              labelId="recurring-hours-from-label"
                              label={`Starting at (${authorTimeLabel})`}
                              value={hoursFrom}
                              onChange={(e) => {
                                markDirty();
                                setHoursFrom(Number(e.target.value));
                              }}
                              disabled={builderFullyDisabled}
                            >
                              {HOUR_OPTIONS_12H.filter((opt) => opt.value !== hoursTo).map(
                                (opt) => (
                                  <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </MenuItem>
                                )
                              )}
                            </Select>
                          </FormControl>

                          <FormControl size="small" fullWidth>
                            <InputLabel id="recurring-hours-to-label">
                              Ending at ({authorTimeLabel})
                              {timeFrameCrossesMidnight ? ' — next day' : ''}
                            </InputLabel>
                            <Select
                              labelId="recurring-hours-to-label"
                              label={`Ending at (${authorTimeLabel})${timeFrameCrossesMidnight ? ' — next day' : ''}`}
                              value={hoursTo}
                              renderValue={(value) =>
                                formatTimeFrameEndDropdownLabel(Number(value), hoursFrom)
                              }
                              onChange={(e) => {
                                markDirty();
                                setHoursTo(Number(e.target.value));
                              }}
                              disabled={builderFullyDisabled}
                            >
                              {HOUR_OPTIONS_12H.filter((opt) => opt.value !== hoursFrom).map(
                                (opt) => (
                                  <MenuItem key={opt.value} value={opt.value}>
                                    {formatTimeFrameEndDropdownLabel(opt.value, hoursFrom)}
                                  </MenuItem>
                                )
                              )}
                            </Select>
                          </FormControl>
                        </Box>

                        {timeFrameDurationHours > 0 && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 1, display: 'block' }}
                          >
                            Time frame: {timeFrameDurationHours}{' '}
                            {timeFrameDurationHours === 1 ? 'hr' : 'hrs'}
                            {timeFrameCrossesMidnight ? ' (crosses midnight)' : ''}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* Frequency */}
                  <Box sx={recurringFieldRowSx}>
                    <Typography variant="body2" color="text.secondary">
                      Every
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <FormControl size="small" sx={{ width: 88 }}>
                        {frequencyUnit === 'hours' ? (
                          <Select
                            value={recurringHours}
                            onChange={(e) => {
                              markDirty();
                              setRecurringHours(e.target.value);
                            }}
                            disabled={builderFullyDisabled}
                          >
                            {RECURRING_HOUR_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        ) : (
                          <Select
                            value={recurringMinutes}
                            onChange={(e) => {
                              markDirty();
                              setRecurringMinutes(e.target.value);
                            }}
                            disabled={builderFullyDisabled}
                          >
                            {RECURRING_MINUTE_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </FormControl>

                      <ToggleButtonGroup
                        value={frequencyUnit}
                        exclusive
                        onChange={handleFrequencyUnitChange}
                        size="small"
                        disabled={builderFullyDisabled}
                      >
                        <ToggleButton value="minutes">Minutes</ToggleButton>
                        <ToggleButton value="hours">Hours</ToggleButton>
                      </ToggleButtonGroup>
                    </Stack>
                  </Box>

                  {/* On */}
                  {renderOnSelector(
                    onType,
                    setOnType,
                    recurringDow,
                    setRecurringDow,
                    recurringDom,
                    setRecurringDom,
                    recurringMonth,
                    setRecurringMonth
                  )}

                  {/* Starting Minute (only for hours frequency) */}
                  {frequencyUnit === 'hours' && (
                    <Box sx={recurringFieldRowSx}>
                      <Typography variant="body2" color="text.secondary">
                        Starting Minute ({authorTimeLabel})
                      </Typography>
                      <FormControl size="small" sx={{ width: 88 }}>
                        <Select
                          value={startingMinute}
                          onChange={(e) => {
                            markDirty();
                            setStartingMinute(Number(e.target.value));
                          }}
                          disabled={builderFullyDisabled}
                        >
                          {STARTING_MINUTE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {timeframeConfirmPending && onConfirmTimeframe && (
                    <Box>
                      <Button variant="contained" color="error" onClick={onConfirmTimeframe}>
                        Confirm time frame
                      </Button>
                    </Box>
                  )}
                </Stack>
              </Box>
            </Stack>
          )}

          {/* ============================================================== */}
          {/* Fixed-time Mode (single date + time) */}
          {/* ============================================================== */}
          {mode === 'fixed-time' && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Date and time are entered in {formatTimezoneLabel(timeZone)}.
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Select Date ({authorTimeLabel}):
                  </Typography>
                  <DatePicker
                    value={fixedDate}
                    onChange={(d) => {
                      if (d) {
                        markDirty();
                        setFixedDate(d);
                      }
                    }}
                    minDate={new Date()}
                    disabled={nonTimeframeDisabled}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Select Time ({authorTimeLabel}):
                  </Typography>
                  <TimePicker
                    value={fixedTime}
                    onChange={(t) => {
                      if (t) {
                        markDirty();
                        setFixedTime(t);
                      }
                    }}
                    disabled={nonTimeframeDisabled}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                </Box>
              </Stack>
            </Stack>
          )}

          {/* ============================================================== */}
          {/* Recurring Fixed-time Mode (specific time, repeating on days) */}
          {/* ============================================================== */}
          {mode === 'recurring-fixed-time' && (
            <Stack spacing={2.5}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                  Select Time At ({authorTimeLabel}):
                </Typography>
                <FormControl size="small" sx={{ minWidth: 70 }}>
                  <Select
                    value={rfHour}
                    onChange={(e) => {
                      markDirty();
                      setRfHour(Number(e.target.value));
                    }}
                    disabled={nonTimeframeDisabled}
                  >
                    {FIXED_HOUR_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography>:</Typography>
                <FormControl size="small" sx={{ minWidth: 70 }}>
                  <Select
                    value={rfMinute}
                    onChange={(e) => {
                      markDirty();
                      setRfMinute(Number(e.target.value));
                    }}
                    disabled={nonTimeframeDisabled}
                  >
                    {FIXED_MINUTE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              {renderOnSelector(
                rfOnType,
                setRfOnType,
                rfDow,
                setRfDow,
                rfDom,
                setRfDom,
                rfMonth,
                setRfMonth
              )}
            </Stack>
          )}
        </Paper>

        {mode === 'recurring' && !recurringValidation.valid && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {recurringValidation.errors.join(' ')}
          </Alert>
        )}

        {showLegacyMigrationNotice && legacyMigration?.needsMigration && (
          <Box sx={{ mt: 2 }}>
            <CronScheduleMigrationNotice migration={legacyMigration} compact />
          </Box>
        )}
      </Box>
    );
  }
);
